import { Component, Input, OnInit } from '@angular/core';
import { SharedService } from '../../services/shared.service';

interface Pick { id: number; label: string; }

/**
 * Widget Groups, inside Settings > Users (mockup: no menu item of its own since 2026-10-19; the
 * old /share-widgets URL opens Users). Your widget groups on the left; the
 * editor on the right with the title, the users and - kept from the old page (agreed) - the
 * widgets the group shares, both picked from a searchable list with "Select all".
 * Group members see the widgets on the group's page (/groupwidget/:id). Same API as before.
 */
@Component({
  selector: 'app-share-widgets',
  templateUrl: './share-widgets.component.html',
  styleUrls: ['./share-widgets.component.scss']
})
export class ShareWidgetsComponent implements OnInit {
  // Inside Users: no page heading of its own.
  @Input() embedded = false;
  groups: any[] = [];
  users: Pick[] = [];
  widgets: Pick[] = [];
  loaded = false;

  editing = false;          // the editor shows a group (new or existing)
  groupId: any = '';
  title = '';
  selectedUsers: { [id: number]: boolean } = {};
  selectedWidgets: { [id: number]: boolean } = {};
  userSearch = '';
  widgetSearch = '';
  titleError = '';
  usersError = false;
  widgetsError = false;
  saving = false;
  message = '';
  deleting = false;

  constructor(private sharedService: SharedService) { }

  ngOnInit() {
    this.sharedService.getUsersList().subscribe(data =>
      this.users = (data || []).map(u => ({ id: +u.user_id, label: u.user_full_name })));
    this.sharedService.getWidgets().subscribe(data =>
      this.widgets = Object.keys(data || {}).map(k => data[k]).map(w => ({ id: +w.widget_id, label: w.title })));
    this.loadGroups();
  }

  private loadGroups() {
    this.sharedService.getGroupedWidget().subscribe(data => {
      this.groups = (data || []).slice().sort((a, b) => (a.group_title || '').localeCompare(b.group_title || ''));
      this.loaded = true;
    });
  }

  // ---- Pickers ----

  shown(list: Pick[], search: string): Pick[] {
    const q = search.trim().toLowerCase();
    return !q ? list : list.filter(p => (p.label || '').toLowerCase().indexOf(q) !== -1);
  }

  count(selected: { [id: number]: boolean }): number {
    return Object.keys(selected).filter(k => selected[k]).length;
  }

  allSelected(list: Pick[], selected: { [id: number]: boolean }): boolean {
    return list.length > 0 && list.every(p => selected[p.id]);
  }

  selectAll(list: Pick[], selected: { [id: number]: boolean }, on: boolean) {
    list.forEach(p => selected[p.id] = on);
  }

  // ---- Editor ----

  create() {
    this.reset();
    this.editing = true;
  }

  edit(group: any) {
    this.reset();
    this.sharedService.getSingleGroupData(group.id).subscribe(data => {
      this.groupId = data.id;
      this.title = data.group_title;
      (data.group_users || []).forEach(u => this.selectedUsers[+u.widget_user_id] = true);
      (data.widget_list || []).forEach(w => this.selectedWidgets[+w.widget_id] = true);
      this.editing = true;
    });
  }

  cancel() {
    this.reset();
  }

  private reset() {
    this.editing = false;
    this.groupId = '';
    this.title = '';
    this.selectedUsers = {};
    this.selectedWidgets = {};
    this.userSearch = '';
    this.widgetSearch = '';
    this.titleError = '';
    this.usersError = false;
    this.widgetsError = false;
    this.message = '';
    this.deleting = false;
  }

  save() {
    this.titleError = this.title.trim() ? '' : 'Enter a group title.';
    this.usersError = !this.count(this.selectedUsers);
    this.widgetsError = !this.count(this.selectedWidgets);
    if (this.titleError || this.usersError || this.widgetsError) {
      return;
    }
    const selected = (map: { [id: number]: boolean }) => Object.keys(map).filter(k => map[k]).map(k => +k);
    const users = selected(this.selectedUsers).map(id => ({ user_id: id, user_full_name: (this.users.filter(u => u.id === id)[0] || { label: '' }).label }));
    const isUpdate = !!this.groupId;
    this.saving = true;
    this.sharedService.postGroupedWidget({
      groupId: this.groupId,
      groupTitle: this.title.trim(),
      groupUsers: users,
      groupedWidgets: selected(this.selectedWidgets),
    }).subscribe(() => {
      this.saving = false;
      this.reset();
      this.message = isUpdate ? 'Group updated.' : 'Group created.';
      this.loadGroups();
    }, () => this.saving = false);
  }

  remove() {
    this.saving = true;
    this.sharedService.postDeleteGroupWidget({ id: this.groupId }).subscribe(() => {
      this.saving = false;
      this.reset();
      this.message = 'Group deleted.';
      this.loadGroups();
    }, () => this.saving = false);
  }
}
